"use client";

import { useState, useEffect, useRef } from "react";
import {
  Phone,
  PhoneOff,
  Save,
  Loader2,
  AlertCircle,
  X,
  MicOff,
  Mic,
} from "lucide-react";
import { useCommsPanel } from "../CommsProvider";
import { CallScriptView } from "../call-script/CallScriptView";
import { useWebRTCCall } from "@/lib/webrtc/useWebRTCCall";

interface CallComposerProps {
  onSent: () => void;
}

export function CallComposer({ onSent }: CallComposerProps) {
  const { activeLeadId, notifyLeadChanged } = useCommsPanel();
  const [phase, setPhase] = useState<"ready" | "initiating" | "calling" | "outcome">("ready");
  const [callId, setCallId] = useState<string | null>(null);
  const [entryId, setEntryId] = useState<string | null>(null);
  const [initiateError, setInitiateError] = useState<string | null>(null);
  const [outcome, setOutcome] = useState("connected");
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const [sectionNotes, setSectionNotes] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const callStartTime = useRef<number | null>(null);
  const [elapsed, setElapsed] = useState(0);

  const webrtc = useWebRTCCall();

  // Listen for WebRTC status changes to auto-transition phases
  useEffect(() => {
    if (webrtc.status === "active" && phase === "initiating") {
      setPhase("calling");
      callStartTime.current = Date.now();
    }
    if (webrtc.status === "ended" && phase === "calling") {
      handleEndCall();
    }
    if (webrtc.status === "error" && phase === "initiating") {
      setInitiateError(webrtc.error ?? "Call failed");
    }
    if (webrtc.status === "error" && phase === "calling") {
      handleEndCall();
    }
  }, [webrtc.status]);

  // Initiate call: fetch lead phone, then WebRTC + backend ContactAttempt in parallel
  async function initiateCall() {
    if (!activeLeadId) return;
    setPhase("initiating");
    setInitiateError(null);

    try {
      // Fetch lead phone number first
      const leadRes = await fetch(`/api/leads/${activeLeadId}`);
      if (!leadRes.ok) throw new Error("Failed to load lead details");
      const leadData = await leadRes.json();
      const phone = leadData.phone;
      if (!phone) { setInitiateError("Lead has no phone number"); return; }

      // Start WebRTC call and create backend ContactAttempt in parallel
      const [, backendResult] = await Promise.all([
        webrtc.startCall(phone),
        fetch(`/api/leads/${activeLeadId}/timeline/call/initiate`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        }).then(async (res) => {
          if (res.ok) return res.json();
          const err = await res.json().catch(() => ({ error: { message: "Failed to initiate call" } }));
          throw new Error(err.error?.message ?? "Failed to initiate call");
        }),
      ]);

      setCallId(backendResult.callId);
      setEntryId(backendResult.entry?.id ?? null);

      // If WebRTC already connected during the await, transition now
      if (webrtc.status === "active") {
        callStartTime.current = Date.now();
        setPhase("calling");
      }
      // Otherwise the useEffect above will handle transition when status becomes "active"
    } catch (err) {
      const message = err instanceof Error ? err.message : "Network error — could not initiate call";
      setInitiateError(message);
      // Clean up WebRTC if backend failed
      webrtc.endCall();
    }
  }

  // Timer for call duration
  useEffect(() => {
    if (phase !== "calling") return;
    const interval = setInterval(() => {
      if (callStartTime.current) {
        setElapsed(Math.floor((Date.now() - callStartTime.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [phase]);

  function formatTime(seconds: number) {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  }

  function handleEndCall(scriptNotes?: Record<string, string>) {
    if (scriptNotes) setSectionNotes(scriptNotes);
    if (callStartTime.current) {
      setDuration(String(Math.floor((Date.now() - callStartTime.current) / 1000)));
    }
    webrtc.endCall();
    setPhase("outcome");
  }

  async function handleSubmit() {
    if (!activeLeadId || saving) return;
    setSaving(true);
    try {
      const res = await fetch(`/api/leads/${activeLeadId}/timeline/call/outcome`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          entryId,
          callId,
          outcome,
          duration: duration ? Number(duration) : undefined,
          notes: notes.trim() || undefined,
          sectionNotes: Object.keys(sectionNotes).length > 0 ? sectionNotes : undefined,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: { message: "Failed to save call outcome" } }));
        throw new Error(err.error?.message ?? "Failed to save call outcome");
      }
      notifyLeadChanged();
      setPhase("ready");
      setCallId(null);
      setEntryId(null);
      callStartTime.current = null;
      setElapsed(0);
      setOutcome("connected");
      setDuration("");
      setNotes("");
      setSectionNotes({});
      onSent();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to save call outcome";
      setPhase("outcome");
      alert(msg);
    } finally {
      setSaving(false);
    }
  }

  // ── Ready phase — review script, then click to call ──
  if (phase === "ready") {
    return (
      <div className="flex max-h-[60vh] flex-col border-t border-gray-200 bg-white">
        <div className="flex-1 overflow-y-auto px-3 pt-3 pb-1">
          <CallScriptView onEndCall={() => {}} />
        </div>
        <div className="sticky bottom-0 flex items-center gap-2 border-t border-gray-100 bg-white px-3 py-2.5">
          <button
            onClick={initiateCall}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 py-2.5 text-sm font-medium text-white hover:bg-green-700"
          >
            <Phone className="h-4 w-4" />
            Start Call
          </button>
          <button
            onClick={onSent}
            className="flex items-center justify-center rounded-lg border border-gray-200 px-3 py-2.5 text-sm text-gray-500 hover:bg-gray-50"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // ── Initiating phase — connecting WebRTC ──
  if (phase === "initiating") {
    if (initiateError) {
      return (
        <div className="border-t border-gray-200 bg-red-50 px-3 py-3">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <p className="text-xs font-medium text-red-700">Call failed</p>
          </div>
          <p className="mt-1 text-xs text-red-600">{initiateError}</p>
          <div className="mt-2 flex gap-2">
            <button
              onClick={initiateCall}
              className="text-xs font-medium text-red-700 hover:text-red-900"
            >
              Retry
            </button>
            <button
              onClick={() => { setPhase("ready"); setInitiateError(null); }}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Back to script
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="border-t border-gray-200 bg-yellow-50 px-3 py-3">
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-yellow-600" />
          <p className="text-xs font-medium text-yellow-800">
            {webrtc.status === "connecting" ? "Connecting..." : webrtc.status === "ringing" ? "Ringing..." : "Dialing..."}
          </p>
        </div>
      </div>
    );
  }

  // ── Calling phase — active call with WebRTC controls + script ──
  if (phase === "calling") {
    return (
      <div className="flex max-h-[60vh] flex-col border-t border-gray-200 bg-green-50">
        <div className="sticky top-0 z-10 flex items-center justify-between bg-green-50 px-3 py-2.5 border-b border-green-100">
          <div className="flex items-center gap-2">
            <div className="animate-pulse rounded-full bg-green-500 p-1.5">
              <Phone className="h-3.5 w-3.5 text-white" />
            </div>
            <p className="text-xs font-medium text-green-800">Call in progress</p>
            <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-mono text-green-700">
              {formatTime(elapsed)}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {/* Mute toggle */}
            <button
              onClick={webrtc.toggleMute}
              className={`flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors ${
                webrtc.isMuted
                  ? "bg-red-100 text-red-700 hover:bg-red-200"
                  : "bg-green-100 text-green-700 hover:bg-green-200"
              }`}
              title={webrtc.isMuted ? "Unmute" : "Mute"}
            >
              {webrtc.isMuted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            </button>
            {/* End call */}
            <button
              onClick={() => handleEndCall()}
              className="flex items-center gap-1.5 rounded-lg bg-red-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-600"
            >
              <PhoneOff className="h-3.5 w-3.5" />
              End
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-3">
          <CallScriptView onEndCall={(sn) => handleEndCall(sn)} />
        </div>
      </div>
    );
  }

  // ── Outcome phase — record results ──
  return (
    <div className="border-t border-gray-200 bg-white px-3 py-3">
      <p className="mb-2 text-xs font-medium text-green-700">Record Call Outcome</p>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="mb-1 block text-[10px] font-medium text-gray-600">Outcome</label>
          <select
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
          >
            <option value="connected">Connected</option>
            <option value="no_answer">No Answer</option>
            <option value="voicemail">Voicemail</option>
            <option value="busy">Busy</option>
          </select>
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-medium text-gray-600">Duration (seconds)</label>
          <input
            type="number"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs"
            placeholder="e.g. 120"
          />
        </div>
      </div>
      <div className="mt-2">
        <label className="mb-1 block text-[10px] font-medium text-gray-600">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="w-full rounded-lg border border-gray-300 px-2 py-1.5 text-xs resize-none"
          placeholder="Call notes..."
        />
      </div>
      <div className="mt-2 flex items-center justify-between">
        <button
          onClick={() => setPhase("calling")}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          Back to call
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
        >
          <Save className="h-3.5 w-3.5" />
          {saving ? "Saving..." : "Save Call"}
        </button>
      </div>
    </div>
  );
}
